import { type ReactNode, useState } from 'react';
import { cn } from '../utils/cn';

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export type TabsVariant = 'underline' | 'arrow' | 'pills';
export type TabStatus = 'default' | 'danger' | 'success' | 'warning';

export interface TabsProps {
  tabs: TabItem[];
  defaultId?: string;
  activeId?: string;
  onChange?: (id: string) => void;
  variant?: TabsVariant;
  className?: string;
  /** Per-tab status override (e.g. mark a tab red) */
  tabStatus?: Record<string, TabStatus>;
  /** Only render the tab bar; let the caller render content externally */
  barOnly?: boolean;
}

export function Tabs({
  tabs,
  defaultId,
  activeId,
  onChange,
  variant = 'arrow',
  className,
  tabStatus,
  barOnly,
}: TabsProps) {
  const [internal, setInternal] = useState(defaultId ?? tabs[0]?.id);
  const active = activeId ?? internal;
  const current = tabs.find((t) => t.id === active) ?? tabs[0];

  const select = (id: string) => {
    if (activeId === undefined) setInternal(id);
    onChange?.(id);
  };

  if (variant === 'underline') {
    return (
      <div className={className}>
        <div className="flex gap-1 border-b border-[var(--color-border)]">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={t.disabled}
              onClick={() => select(t.id)}
              className={cn(
                'inline-flex h-10 items-center gap-2 px-4 text-sm font-medium transition-colors disabled:opacity-50',
                active === t.id
                  ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-primary)]'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
        <div className="pt-4">{current?.content}</div>
      </div>
    );
  }

  if (variant === 'pills') {
    return (
      <div className={className}>
        <div className="flex flex-wrap gap-1 rounded-lg bg-[var(--color-bg-card-hover)] p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={t.disabled}
              onClick={() => select(t.id)}
              className={cn(
                'inline-flex h-9 items-center gap-2 rounded-md px-4 text-sm font-medium transition-colors disabled:opacity-50',
                active === t.id
                  ? 'bg-[var(--color-bg-card)] text-[var(--color-primary)] shadow-sm'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
        <div className="pt-4">{current?.content}</div>
      </div>
    );
  }

  // arrow variant — chevron-shaped breadcrumb-style tabs
  const activeIndex = tabs.findIndex((t) => t.id === active);

  return (
    <div className={className}>
      <div className="kss-arrow-tabs flex items-stretch overflow-x-auto">
        {tabs.map((t, i) => {
          const isActive = t.id === active;
          const isPast = i < activeIndex;
          const isFirst = i === 0;
          const isLast = i === tabs.length - 1;
          const status = tabStatus?.[t.id] ?? 'default';

          const baseBg = isActive
            ? 'bg-[var(--color-primary)] text-[var(--color-primary-fg)]'
            : isPast
              ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
              : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]';

          const overrideBg =
            status === 'danger'
              ? 'bg-[var(--color-danger)] text-white'
              : status === 'warning'
                ? 'bg-[var(--color-warning)] text-white'
                : status === 'success'
                  ? 'bg-[var(--color-success)] text-white'
                  : '';

          const numberBg = isActive
            ? 'bg-[var(--color-primary-fg)]/20 text-[var(--color-primary-fg)]'
            : isPast
              ? 'bg-[var(--color-primary)] text-[var(--color-primary-fg)]'
              : 'bg-[var(--color-bg-card-hover)] text-[var(--color-text-muted)]';

          const numberOverride =
            status === 'danger' || status === 'warning' || status === 'success'
              ? 'bg-white/25 text-white'
              : '';

          return (
            <button
              key={t.id}
              type="button"
              disabled={t.disabled}
              onClick={() => select(t.id)}
              className={cn(
                'kss-arrow-tab relative flex shrink-0 items-center justify-center gap-2 whitespace-nowrap py-2.5 text-sm font-medium transition-colors disabled:opacity-50',
                isActive ? 'z-[3]' : isPast ? 'z-[2]' : 'z-[1]',
                overrideBg || baseBg,
                isFirst ? 'pl-5' : 'pl-6 -ml-3',
                isLast ? 'pr-5' : 'pr-10'
              )}
              style={{
                clipPath: isLast
                  ? 'none'
                  : 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)',
              }}
            >
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold',
                  numberOverride || numberBg
                )}
              >
                {i + 1}
              </span>
              {t.icon}
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>
      {!barOnly && <div className="pt-4">{current?.content}</div>}
    </div>
  );
}

export default Tabs;
